/**
 * Print a document from a URL via blob + iframe (works for signed cross-origin URLs).
 */
export interface PrintOptions {
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export const handlePrintFile = async (
  url: string | undefined,
  fileName?: string,
  options?: PrintOptions,
): Promise<void> => {
  if (!url) {
    console.error("Cannot print: URL is undefined");
    options?.onError?.(new Error("URL is undefined"));
    return;
  }

  const cleanup = (iframe: HTMLIFrameElement, blobUrl: string) => {
    setTimeout(() => {
      try {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
        window.URL.revokeObjectURL(blobUrl);
      } catch {
        // ignore
      }
    }, 2000);
  };

  try {
    options?.onStart?.();

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Failed to fetch file for printing");
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.src = blobUrl;

    document.body.appendChild(iframe);

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        options?.onComplete?.();
      } catch (e) {
        console.error("Iframe print failed", e);
        const printWindow = window.open(blobUrl, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
          };
        }
        options?.onComplete?.();
      }

      cleanup(iframe, blobUrl);
    };

    iframe.onerror = () => {
      options?.onError?.(new Error("Failed to load document for printing"));
      cleanup(iframe, blobUrl);
      window.open(url, "_blank");
    };
  } catch (error) {
    console.error("Error printing file:", fileName ?? "(unknown)", error);
    options?.onError?.(
      error instanceof Error ? error : new Error(String(error)),
    );
    options?.onComplete?.();
    window.open(url, "_blank");
  }
};
